import axios from '../axios';

import type { Track } from '../interfaces/track';
import type { Album } from '../interfaces/albums';
import type { Artist } from '../interfaces/artist';
import type { Pagination } from '../interfaces/api';
import type { Playlist } from '../interfaces/playlists';
import type { Episode } from '../interfaces/episode';
import {
  mapJioSaavnSongToSpotifyTrack,
  mapJioSaavnAlbumToSpotifyAlbum,
  mapJioSaavnArtistToSpotifyArtist,
  mapJioSaavnPlaylistToSpotifyPlaylist
} from '../utils/jiosaavn-adapters';

const SEARCH_MAX_LIMIT = 10;
const clampLimit = (limit?: number) => Math.min(limit ?? SEARCH_MAX_LIMIT, SEARCH_MAX_LIMIT);

const lyricsRequestCache = new Map<string, Promise<any>>();
const querySearchCache = new Map<string, { timestamp: number; data: any }>();
const QUERY_CACHE_TTL = 120000; // 2 minutes

const getLyricsSearch = (query: string, limit: number = 3) => {
  const key = `${query.trim().toLowerCase()}_${limit}`;
  if (!lyricsRequestCache.has(key)) {
    const promise = axios
      .get<any>('/search/lyrics', { params: { query, limit } })
      .catch(() => null)
      .finally(() => {
        setTimeout(() => lyricsRequestCache.delete(key), 8000);
      });
    lyricsRequestCache.set(key, promise);
  }
  return lyricsRequestCache.get(key)!;
};

export const querySearch = async (params: {
  q: string;
  type: string;
  market?: string;
  limit?: number;
  offset?: number;
}) => {
  const cleanQ = (params.q || '').trim();
  const limit = Math.min(params.limit || 10, 10);
  const offset = params.offset || 0;
  const page = Math.floor(offset / limit) + 1;

  const emptyResults: any = {
    albums: { href: '', items: [], limit, offset: 0, total: 0, previous: null, next: null },
    tracks: { href: '', items: [], limit, offset: 0, total: 0, previous: null, next: null },
    artists: { href: '', items: [], limit, offset: 0, total: 0, previous: null, next: null },
    playlists: { href: '', items: [], limit, offset: 0, total: 0, previous: null, next: null }
  };

  if (!cleanQ) {
    return { data: emptyResults };
  }

  const cacheKey = `${cleanQ.toLowerCase()}__${params.type}__${limit}__${page}`;
  const cached = querySearchCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < QUERY_CACHE_TTL) {
    return { data: cached.data };
  }

  const types = params.type.split(',');
  const results: any = {
    albums: { href: '', items: [], limit, offset: 0, total: 0, previous: null, next: null },
    tracks: { href: '', items: [], limit, offset: 0, total: 0, previous: null, next: null },
    artists: { href: '', items: [], limit, offset: 0, total: 0, previous: null, next: null },
    playlists: { href: '', items: [], limit, offset: 0, total: 0, previous: null, next: null }
  };

  const fetchType = async (t: string) => {
    try {
      if (t === 'track') {
        const fetchSongs = axios
          .get<any>('/search/songs', { params: { query: params.q, limit, page } })
          .catch(() => ({ data: { data: { results: [], total: 0 } } }));

        const wordCount = params.q.trim().split(/\s+/).length;
        const shouldSearchLyrics = wordCount >= 2 && page === 1;

        const fetchLyrics = shouldSearchLyrics
          ? getLyricsSearch(params.q, 3)
          : Promise.resolve(null);

        let [res, lyricsRes] = await Promise.all([fetchSongs, fetchLyrics]);
        let items = res.data?.data?.results || [];

        // Fallback to lyrics search if title search returned 0 items
        if (items.length === 0 && !lyricsRes && page === 1) {
          lyricsRes = await getLyricsSearch(params.q, 3);
        }

        const lyricItems = lyricsRes?.data?.data?.results || [];
        const mappedTracks = items.map(mapJioSaavnSongToSpotifyTrack);
        const mappedLyricTracks = lyricItems.map(mapJioSaavnSongToSpotifyTrack);

        const merged: Track[] = [];
        const seenIds = new Set<string>();

        for (const lt of mappedLyricTracks) {
          seenIds.add(lt.id);
          merged.push(lt);
        }

        for (const tr of mappedTracks) {
          if (!seenIds.has(tr.id)) {
            seenIds.add(tr.id);
            merged.push(tr);
          } else {
            const existing = merged.find((m) => m.id === tr.id);
            if (existing) {
              existing.isLyricsMatch = true;
            }
          }
        }

        results.tracks.items = merged.slice(0, limit);
        results.tracks.total = Math.max(res.data?.data?.total || 0, merged.length);
      } else if (t === 'album') {
        const res = await axios.get<any>('/search/albums', { params: { query: params.q, limit, page } });
        const items = res.data?.data?.results || [];
        results.albums.items = items.map(mapJioSaavnAlbumToSpotifyAlbum);
        results.albums.total = res.data?.data?.total || 0;
      } else if (t === 'artist') {
        const res = await axios.get<any>('/search/artists', { params: { query: params.q, limit, page } });
        const items = res.data?.data?.results || [];
        results.artists.items = items.map(mapJioSaavnArtistToSpotifyArtist);
        results.artists.total = res.data?.data?.total || 0;
      } else if (t === 'playlist') {
        const res = await axios.get<any>('/search/playlists', { params: { query: params.q, limit, page } });
        const items = res.data?.data?.results || [];
        results.playlists.items = items.map(mapJioSaavnPlaylistToSpotifyPlaylist);
        results.playlists.total = res.data?.data?.total || 0;
      }
    } catch (e) {
      console.error(e);
    }
  };

  await Promise.all(types.map(fetchType));

  querySearchCache.set(cacheKey, { timestamp: Date.now(), data: results });

  return { data: results };
};

export const searchEpisodes = async (params: {
  q: string;
  limit?: number;
  offset?: number;
  market?: string;
}) => {
  const limit = Math.min(params.limit || 10, 10);
  const offset = params.offset || 0;
  const page = Math.floor(offset / limit) + 1;
  
  try {
    const res = await axios.get<any>('/search/shows', { params: { query: params.q, limit, page } });
    const items = res.data?.data?.results || [];
    // Note: Assuming mapJioSaavnShowToSpotifyEpisode is imported. I will add it to imports.
    // wait, mapJioSaavnShowToSpotifyEpisode is NOT imported. I must import it!
    return { data: { episodes: { href: '', items: items.map(require('../utils/jiosaavn-adapters').mapJioSaavnShowToSpotifyEpisode), limit, offset, total: res.data?.data?.total || 0, previous: null, next: null } } };
  } catch (e) {
    return { data: { episodes: { href: '', items: [], limit: 10, offset: 0, total: 0, previous: null, next: null } } };
  }
};
